import { Theme } from '@fluentui/react-components';
import * as React from 'react';

import { CustomTokens, spirisLightTheme } from '../../styles';

export interface AppThemeResult {
    className: string;
    theme: Theme & CustomTokens;
    style: React.CSSProperties;
}

/**
 * List of CustomTokens keys that should be exposed as CSS variables.
 * This is the single source of truth for which tokens become --app-* variables.
 *
 * Each key from CustomTokens listed here will be converted to a CSS variable:
 *   headerBackground → --app-header-background
 *   userMessageText  → --app-user-message-text
 */
const CSS_VAR_TOKEN_KEYS: Array<keyof CustomTokens> = [
    'headerBackground',
    'navTabActiveBackground',
    'navTabHoverBackground',
    'userMessageBackground',
    'botIconBackground',
    'supportMessageBackground',
    'shadowCard',
    'shadowCardSubtle',
    'sendButtonActiveIconColor',
    'hoverAccordionBackground',
];

/**
 * Converts camelCase to kebab-case
 * Example: headerBackground → header-background
 */
const toKebabCase = (str: string): string => {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

/**
 * Generates CSS custom properties from theme tokens.
 * Token names are automatically converted from camelCase to --app-kebab-case.
 */
const toAppCssVars = (theme: CustomTokens): React.CSSProperties => {
    const vars: Record<string, string> = {};

    for (const key of CSS_VAR_TOKEN_KEYS) {
        const cssVarName = `--app-${toKebabCase(key)}`;
        vars[cssVarName] = theme[key];
    }

    return vars;
};

export const useAppTheme = (): AppThemeResult => {
    const theme = spirisLightTheme;
    const style = React.useMemo(() => toAppCssVars(theme), [theme]);

    // Fluent UI components like Dialog render in a Portal (outside the FluentProvider DOM subtree).
    // Since our --app-* CSS variables are applied via inline styles on FluentProvider, they won't
    // be inherited by portaled content unless we also apply them at the document level.
    React.useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const root = document.documentElement;
        const cssVarKeys = Object.keys(style).filter((key) => key.startsWith('--app-'));

        for (const cssVarKey of cssVarKeys) {
            const value = style[cssVarKey as keyof React.CSSProperties];
            if (typeof value === 'string') {
                root.style.setProperty(cssVarKey, value);
            }
        }

        return () => {
            for (const cssVarKey of cssVarKeys) {
                root.style.removeProperty(cssVarKey);
            }
        };
    }, [style]);

    return {
        className: 'app-container',
        theme,
        style,
    };
};
